// Reservation.jsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import frLocale from "date-fns/locale/fr";
import { useSnackbar } from "notistack";

import { PersonAdd as PersonAddIcon } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Autocomplete as MUIAutocomplete,
  Box,   Button,
  Card, CardContent,   Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,   IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography 
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDoc, collection, doc, getDocs, onSnapshot,updateDoc } from "firebase/firestore";

import AddDiscountDialog from "../components/Booking/AddDiscountDialog";
import AddFeeDialog from "../components/Booking/AddFeeDialog";
import CustomerForm from "../components/CustomerForm";
import LocationForm from "../components/LocationForm";
import { calculateTotalDiscounts } from "../components/utils";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";

function Reservation() {
  const { currentUser } = useAuth();
  const navigate = useNavigate(); // Initialiser le hook navigate
  const { t } = useTranslation();
  const [catalogItems, setCatalogItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [openLocationModal, setOpenLocationModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [locationToRemove, setLocationToRemove] = useState(null);

  // Dans le state, on ajoute un état pour les workflows
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowTasks, setWorkflowTasks] = useState([]); // Les tâches du workflow sélectionné

  const location = useLocation();

  // Vérifie si on est en mode édition
  const reservationData = location.state?.reservationData || null;
  const isEditing = !!reservationData ;
  const initialPaymentPlan =
    reservationData?.paymentPlan && reservationData.paymentPlan !== "null"
      ? reservationData.paymentPlan
      : "Non";

  // États initiaux pour gestion du formulaire
  const [selectedService, setSelectedService] = useState(reservationData?.catalog_id || "");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    reservationData?.selectedDate ? parseISO(reservationData.selectedDate) : null
  );
  const [fees, setFees] = useState(reservationData?.fees || []);
  const [discounts, setDiscounts] = useState(reservationData?.discounts || []);
  const [totalPrice, setTotalPrice] = useState(reservationData?.totalPrice || 0);
  const [locations, setLocations] = useState([]);
  const [servicePrice, setServicePrice] = useState(reservationData?.servicePrice || 0);

  const [paymentPlan, setPaymentPlan] = useState(initialPaymentPlan); // Plan de paiement: Non, 2x, 3x
  const [paymentPercentages, setPaymentPercentages] = useState([100]);
  const [paymentValues, setPaymentValues] = useState([totalPrice]); // Montants en euros basés sur les pourcentages

  const [paymentDetails, setPaymentDetails] = useState([]);
  // Détection si la prestation est entièrement payée
  const isFullyPaid = reservationData && reservationData.paymentDetails?.every(payment => payment.isPaid) || false;

  /**
   *  Fonction pour récupérer le nombre de paiements
   * @returns 
   */
  const getNbPayments = () => {
    if (reservationData.paymentPlan === "2x") {
      return 2;
    } else if (reservationData.paymentPlan === "3x") {
      return 3;
    } else {
      return 1;
    } 
  };
  
  const handleSaveClick = () => {
    if (isFullyPaid) {
      enqueueSnackbar("La prestation a déjà été entièrement réglée. Pour apporter un complément, veuillez créer une nouvelle prestation.", { variant: "warning" });
    } else {
      handleSaveReservation();
    }
  };

  useEffect(() => {
    if (!isEditing || !reservationData) return;

    const orderRef = doc(db, `users/${currentUser.uid}/orders`, reservationData.id);
  
    const unsubscribe = onSnapshot(orderRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const updatedData = docSnapshot.data();
        setPaymentDetails(updatedData.paymentDetails || []);
      }
    });

    return () => unsubscribe();
  }, [currentUser, isEditing, reservationData]);

  useEffect(() => {
    if (!isEditing || !paymentDetails.length || !totalPrice) return;
  
    // On recalcule les pourcentages uniquement pour les paiements non réglés
    const updatedPercentages = paymentDetails.map(payment => {
      const percentage = ((parseFloat(payment.value) / totalPrice) * 100).toFixed(0);
      return percentage;
    });
  
    setPaymentPercentages(updatedPercentages);
  }, [paymentDetails, totalPrice]);

  /**
   * Mise à jour des états lorsqu'on est en mode édition
   */
  useEffect(() => {
    if (isEditing) {
      setSelectedDate(parseISO(reservationData.selectedDate));
      setSelectedClient(
        clients.find(client => client.id === reservationData.client_id) || null
      );

      setLocations(
        (reservationData.locations || []).map(currentLocation => ({
          place: {
            place_id: currentLocation.place_id,
            description: currentLocation.locationWhere
          },
          eventName: currentLocation.eventName,
          isDefault: currentLocation.isDefault,
          eventDate: parseISO(currentLocation.eventDate)
        }))
      );

      setDiscounts(
        (reservationData.discounts || []).map(discount => ({
          discountName: discount.discountName,
          discountAmount: discount.discountAmount,
          isPercentage: discount.isPercentage
        }))
      );

      const calculatePercentage = (paymentValue, totalPrice) => {
        return ((paymentValue / totalPrice) * 100).toFixed(0);
      };

      if(reservationData.paymentDetails.length > 0) {
        const nextPaymentPercentages = reservationData.paymentDetails.map(payment =>
          calculatePercentage(payment.value, reservationData.totalPrice)
        );
        const nextPaymentValues = reservationData.paymentDetails.map(payment => payment.value);
        const nextPaymentDetails = reservationData.paymentDetails.map(payment => ({
          paymentNumber: payment.paymentNumber,
          percentage: calculatePercentage(payment.value, reservationData.totalPrice),
          value: payment.value,
          isPaid: payment.isPaid,
          paymentDate: payment.paymentDate ? format(new Date(payment.paymentDate), "dd/MM/yyyy") : null,
          paymentMode: payment.paymentMode
        }));

        setPaymentPercentages(nextPaymentPercentages);
        setPaymentValues(nextPaymentValues);
        setPaymentDetails(nextPaymentDetails);
      }
    }}, [isEditing,reservationData, clients]);

  const handleOpenConfirmDialog = index => {
    setLocationToRemove(index);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setLocationToRemove(null);
  };

  const handleConfirmRemoveLocation = () => {
    removeLocationByIndex(locationToRemove);
    handleCloseConfirmDialog();
  };

  const hasPaidPayment = paymentDetails.some(payment => payment.isPaid);

  /**
   *  Fonction pour sauvegarder la réservation
   * @returns 
   */
  const handleSaveReservation = async () => {
    if (!selectedService || !selectedClient || !selectedDate) {
      enqueueSnackbar("Merci de remplir tous les champs requis.", {
        variant: "error"
      });
      return;
    }
    
    // **1️ Récupérer les paiements déjà effectués (uniquement en mode édition)**
    const existingPaidPayments = reservationData?.paymentDetails?.filter(p => p.isPaid) || [];

    let updatedUnpaidPayments = [];

    if (isEditing) {
    // **2️ En mode édition : récupérer les valeurs du formulaire**
      updatedUnpaidPayments = paymentDetails
        .filter(payment => !payment.isPaid) // Ne modifier que les paiements non réglés
        .map(payment => ({
          paymentNumber: payment.paymentNumber,
          percentage: payment.percentage,
          value: payment.value, // 🔥 On utilise la valeur saisie dans le formulaire
          isPaid: false
        }));
    } else {
    // **3️ En mode création : Générer les paiements initiaux en fonction du plan de paiement**
      updatedUnpaidPayments = paymentPlan === "Non"
        ? [{
          paymentNumber: 1,
          percentage: 100,
          value: totalPrice,
          isPaid: false
        }]
        : paymentPercentages.map((percentage, index) => ({
          paymentNumber: index + 1,
          percentage: percentage,
          value: ((totalPrice * percentage) / 100).toFixed(2),
          isPaid: false
        }));
    }

    // **4️⃣ Fusionner paiements validés et paiements non réglés**
    const updatedPayments = [...existingPaidPayments, ...updatedUnpaidPayments].sort((a, b) => a.paymentNumber - b.paymentNumber);

    const now = new Date().toISOString();

    const orderData = {
      client_id: selectedClient.id, // Lien vers la collection "clients"
      catalog_id: selectedService, // Lien vers la collection "catalogues"
      servicePrice: servicePrice,
      fees: fees,
      discounts: discounts,
      totalPrice: totalPrice, // Prix total calculé
      selectedDate: selectedDate.toISOString(), // Date de la prestation
      locations: locations.map(location => ({
        place_id: location.place.place_id,
        locationWhere: location.place
          ? location.place.description || location.place.formatted_address
          : "",
        eventName: location.eventName,
        isDefault: location.isDefault || false,
        eventDate: location.eventDate.toISOString()
      })),
      workflow: selectedWorkflow
        ? {
          id: selectedWorkflow.id,
          tasks: workflowTasks // Liste des tâches
        }
        : null, // Workflow sélectionné s'il y en a un
      paymentPlan: paymentPlan !== "Non" ? paymentPlan : null, // Plan de paiement
      paymentDetails: updatedPayments,
      updatedAt: now
    };

    try {
      if (isEditing) {
        // Mode édition : mise à jour du document existant
        const orderRef = doc(db, `users/${currentUser.uid}/orders`, reservationData.id);
        await updateDoc(orderRef, {
          ...orderData,
          createDate: reservationData.createDate || reservationData.createdAt || now
        });
        enqueueSnackbar("Réservation mise à jour avec succès", { variant: "success" });
        navigate("/agenda");
      } else {
        await addDoc(collection(db, `users/${currentUser.uid}/orders`), {
          ...orderData,
          createDate: now
        });
        enqueueSnackbar("Réservation enregistrée avec succès.", {
          variant: "success"
        }); 
        // Redirection vers Agenda après la sauvegarde
        navigate("/agenda");
      } } catch (error) {
      enqueueSnackbar(
        "Erreur lors de l'enregistrement de la réservation : " + error.message,
        { variant: "error" }
      );
    }
      
  };

  useEffect(() => {
    if (!currentUser) return;

    const fetchData = async () => {
      try {
        const [catalogs, customers, workflows] = await Promise.all([
          getDocs(collection(db, `users/${currentUser.uid}/catalog`)),
          getDocs(collection(db, `users/${currentUser.uid}/customers`)),
          getDocs(collection(db, `users/${currentUser.uid}/workflows`))
        ]);

        setCatalogItems(catalogs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setClients(customers.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setWorkflows(workflows.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          tasks: doc.data().tasks || []
        })));
      } catch (error) {
        enqueueSnackbar("Échec du chargement des données : " + error.message, { variant: "error" });
      }
    };

    fetchData();
  }, [currentUser, enqueueSnackbar]);

  /**
   * Calcul du prix total
   */
  useEffect(() => {
    setTotalPrice(
      parseFloat(servicePrice) +
    fees.reduce((acc, fee) => acc + fee.feeAmount, 0) -
    calculateTotalDiscounts(servicePrice, fees, discounts)
    );
  }, [servicePrice, fees, discounts]);

  
  const handleServiceChange = event => {
    const selectedServiceId = event.target.value;
    setSelectedService(selectedServiceId);

    // Trouver le prix de la prestation sélectionnée
    const selectedService = catalogItems.find(
      item => item.id === selectedServiceId
    );
    setServicePrice(selectedService ? selectedService.price : 0);
  };

  const handleClientChange = (event, newValue) => {
    setSelectedClient(newValue);
  };

  const toggleDialog = () => {
    setOpenDialog(!openDialog);
  };

  const handleCustomerSave = newCustomer => {
    setClients(prevClients => [...prevClients, newCustomer]);
    setSelectedClient(newCustomer);
    setOpenDialog(false);
  };

  const handleOpenLocationModal = () => {
    setOpenLocationModal(true);
  };

  const handleCloseLocationModal = () => {
    setOpenLocationModal(false);
  };

  const handleLocationSave = newLocation => {
    setLocations(prevLocations => [...prevLocations, newLocation]);
    enqueueSnackbar("Lieu ajouté avec succès", { variant: "success" });
  };

  const removeLocationByIndex = indexToRemove => {
    setLocations(prevLocations =>
      prevLocations.filter((_, index) => index !== indexToRemove)
    );
  };

  const setLocationAsDefault = indexToSet => {
    setLocations(prevLocations =>
      prevLocations.map((location, index) => ({
        ...location,
        isDefault: index === indexToSet
      }))
    );
  };

  const handleAddDiscount = newDiscount => {
    setDiscounts(prevDiscounts => [...prevDiscounts, newDiscount]);
  };
  const handleAddFee = newFee => {
    setFees(prevFees => [...prevFees, newFee]);
  };

  const removeByIndex = (indexToRemove, type) => {
    if (type === "fee") {
      setFees(prevFees =>
        prevFees.filter((_, index) => index !== indexToRemove)
      );
    } else if (type === "discount") {
      setDiscounts(prevDiscounts =>
        prevDiscounts.filter((_, index) => index !== indexToRemove)
      );
    }
  };

  const handlePaymentPlanChange = e => {
    const selectedPlan = e.target.value;
    setPaymentPlan(selectedPlan);

    let updatedPercentages;
    let updatedValues;

    if (selectedPlan === "2x") {
      updatedPercentages = [50, 50];
    } else if (selectedPlan === "3x") {
      updatedPercentages = [30, 50, 20];
    } else {
      updatedPercentages = [100]; // Paiement unique
    }

    // Mettre à jour les pourcentages et les valeurs
    updatedValues = updatedPercentages.map(percentage => ((totalPrice * percentage) / 100).toFixed(2));

    setPaymentPercentages(updatedPercentages);
    setPaymentValues(updatedValues);

    // Mettre à jour paymentDetails avec ces nouvelles valeurs
    setPaymentDetails(updatedPercentages.map((percentage, index) => ({
      paymentNumber: index + 1,
      percentage: percentage,
      value: updatedValues[index],
      isPaid: false
    })));
  };

  useEffect(() => {
    if (reservationData && totalPrice !== reservationData.totalPrice) {
      const unpaidPayments = reservationData.paymentDetails.filter(payment => !payment.isPaid);
      const paidPayments = reservationData.paymentDetails.filter(payment => payment.isPaid);
      const totalPaid = paidPayments.reduce((sum, payment) => sum + parseFloat(payment.value), 0);
      const newRemaining = totalPrice - totalPaid;

      if (newRemaining <= 0) {
        console.warn("Le montant restant est inférieur ou égal à zéro.");
        return;
      }

      if (unpaidPayments.length === getNbPayments()) {
      // Tous les paiements sont non réglés, appliquer la répartition initiale
        const defaultPercentages = reservationData.paymentPlan === "2x" ? [50, 50] 
          : reservationData.paymentPlan === "3x" ? [30, 50, 20] 
            : [100];

        const updatedPayments = defaultPercentages.map((percentage, index) => ({
          ...unpaidPayments[index],
          value: ((totalPrice * percentage) / 100).toFixed(2),
          percentage
        }));

        setPaymentDetails(updatedPayments);
      } else if (unpaidPayments.length === 1) {
      // Un seul paiement non réglé, lui attribuer tout le montant restant
        const lastUnpaidIndex = reservationData.paymentDetails.findIndex(payment => !payment.isPaid);

        const updatedPayments = reservationData.paymentDetails.map((payment, index) =>
          index === lastUnpaidIndex
            ? { ...payment, value: newRemaining.toFixed(2), percentage: ((newRemaining / totalPrice) * 100).toFixed(2) }
            : payment
        );

        setPaymentDetails(updatedPayments);
      } else if (unpaidPayments.length === 2) {
      // Deux paiements non réglés, répartir la valeur ajoutée/retirée en parts égales
        const valueDifference = totalPrice - reservationData.totalPrice; // Différence de montant
        const halfValue = valueDifference / 2;

        const updatedPayments = reservationData.paymentDetails.map(payment => {
          if (!payment.isPaid) {
            return {
              ...payment,
              value: (parseFloat(payment.value) + halfValue).toFixed(2),
              percentage: (((parseFloat(payment.value) + halfValue) / totalPrice) * 100).toFixed(2)
            };
          }
          return payment;
        });

        setPaymentDetails(updatedPayments);
      }
    }
  }, [totalPrice, reservationData]);

  return (
    <div>
      <Box sx={ { p: 2 } }>
        <Grid container spacing={ 5 }>
          <Grid item xs={ 12 } sm={ 12 } md={ 6 }>
            <Card>
              <CardContent sx={ { p: 2 } }>
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: "1.2rem", mb: 2 }}>
                  {isEditing ? t("reservation.title.edit") : t("reservation.title.new")}
                </Typography>

                {/* Bandeau d'avertissement si la prestation est entièrement payée */}
                {isFullyPaid && (
                  <Box mb={2}>
                    <Alert
                      severity="success"
                      icon={<ErrorIcon fontSize="inherit" sx={{ color: "#820912" }} />}
                      sx={{
                        backgroundColor: "warning.light",
                        color: "black",
                        fontWeight: "bold",
                        alignItems: "center"
                      }}
                    >{t("reservation.warning.fullyPaid")} 
                    </Alert>
                  </Box>
                )}

                <TextField
                  select
                  label={t("reservation.serviceType.label")}
                  value={catalogItems.some(item => item.id === selectedService) ? selectedService : ""}
                  onChange={ handleServiceChange }
                  fullWidth
                >
                  { catalogItems.map(item => (
                    <MenuItem key={ item.id } value={ item.id }>
                      { item.type } : { item.name } - { item.price }€
                    </MenuItem>
                  )) }
                </TextField>

                <div style={ { marginBottom: "1rem", marginTop: "1rem" } }>
                  <MUIAutocomplete
                    options={ clients }
                    getOptionLabel={ option =>
                      option ? `${option.firstname}` : ""
                    }
                    style={ { width: 300 } }
                    onChange={ handleClientChange }
                    //onChange={e => setSelectedClient(e.target.value)}
                    renderInput={ params => (
                      <TextField
                        { ...params }
                        label="Client"
                        variant="outlined"
                      />
                    ) }
                    value={selectedClient || null} 
                    id="client"
                    name="client"
                  />
                </div>
                <div style={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={ <PersonAddIcon /> }
                    onClick={ toggleDialog }
                    size="small"
                  >
                    Nouveau client
                  </Button>{ " " }
                </div>

                <Dialog
                  open={ openDialog }
                  onClose={ toggleDialog }
                  maxWidth="md"
                  fullWidth
                >
                  <CustomerForm
                    open={ openDialog }
                    handleClose={ toggleDialog }
                    onSave={ handleCustomerSave }
                  />
                </Dialog>

                <LocalizationProvider
                  dateAdapter={ AdapterDateFns }
                  adapterLocale={ frLocale }
                >
                  <DatePicker
                    label={t("reservation.date.label")}
                    value={ selectedDate }
                    onChange={ newValue => {
                      setSelectedDate(newValue);
                    } }
                    renderInput={ params => (
                      <TextField { ...params } fullWidth margin="normal" />
                    ) }
                  />
                </LocalizationProvider>
                <div style={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={ handleOpenLocationModal }
                    size="small"
                    startIcon={ <EditLocationIcon /> }
                  >
                    {t("reservation.location.add")}
                  </Button>
                </div>

                <Dialog
                  open={ openLocationModal }
                  onClose={ handleCloseLocationModal }
                  maxWidth="sm"
                  fullWidth
                >
                  <LocationForm
                    selectedDate={ selectedDate }
                    onClose={ handleCloseLocationModal }
                    onSave={ handleLocationSave }
                  />
                </Dialog>

                { locations.length >= 1 ? (
                  <TableContainer component={ Paper }>
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell align="left" sx={ { fontWeight: 500 } }>
                            {t("reservation.location.mainLocation")}
                          </TableCell>
                          <TableCell align="center">{t("reservation.location.date")}</TableCell>
                          <TableCell align="center">{t("reservation.location.place")}</TableCell>
                          <TableCell align="center">{t("reservation.location.note")}</TableCell>
                          { /* <TableCell  align="center">
                                                        Distance
                                                    </TableCell> */ }
                          <TableCell align="center">{t("reservation.location.action")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { locations.map((v, index) => {
                          return (
                            <TableRow key={ v.place_id + index.toString() }>
                              <TableCell align="left">
                                <Checkbox
                                  checked={ v.isDefault }
                                  onChange={ () => setLocationAsDefault(index) }
                                  inputProps={ {
                                    "aria-label": "primary checkbox"
                                  } }
                                />
                              </TableCell>
                              <TableCell align="center">
                                { new Date(v.eventDate).toLocaleString("fr-FR", {
                                  year: "numeric",
                                  month: "numeric",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "numeric"
                                }) }
                              </TableCell>
                              <TableCell align="center">
                                { v.place
                                  ? v.place.description ||
                                    v.place.formatted_address
                                  : "" }
                              </TableCell>
                              <TableCell align="center">
                                { v.eventName }
                              </TableCell>
                              { /* <TableCell  align="center">
                                                                {v.distance || "Loading..."}
                                                            </TableCell> */ }
                              <TableCell align="center">
                                <IconButton
                                  type="button"
                                  variant="contained"
                                  color="secondary"
                                  size="medium"
                                  //disabled={isSubmitting}
                                  onClick={ () => {
                                    //_removeLocationById(index);
                                    //removeLocationByIndex(index);
                                    handleOpenConfirmDialog(index);
                                  } }
                                >
                                  <CancelIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        }) }
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : null }

                <Dialog
                  open={ openConfirmDialog }
                  onClose={ handleCloseConfirmDialog }
                >
                  <DialogTitle>{t("reservation.location.date")}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      {t("reservation.location.deleteConfirm.message")}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={ handleCloseConfirmDialog } color="primary">
                      Annuler
                    </Button>
                    <Button
                      onClick={ handleConfirmRemoveLocation }
                      color="secondary"
                    >
                      {t("common.delete")}
                    </Button>
                  </DialogActions>
                </Dialog>
              </CardContent>
            </Card>
            <Card sx={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
              <CardContent sx={ { p: 2 } }>
                <div style={ { marginBottom: "1rem", marginTop: "1rem" } }>
                  <TextField
                    select
                    label={t("reservation.workflow.label")}
                    value={ selectedWorkflow ? selectedWorkflow.id : "" }
                    onChange={ e => {
                      const selectedId = e.target.value;
                      const workflow = workflows.find(
                        w => w.id === selectedId
                      );
                      setSelectedWorkflow(workflow);
                      setWorkflowTasks(workflow ? workflow.tasks : []); // Charger les tâches du workflow sélectionné
                    } }
                    fullWidth
                  >
                    { workflows.map(workflow => (
                      <MenuItem key={ workflow.id } value={ workflow.id }>
                        { workflow.name } - { workflow.tasks.length } tâche(s)
                      </MenuItem>
                    )) }
                  </TextField>

                  { workflowTasks.length > 0 && (
                    <div style={ { marginTop: "20px" } }>
                      <Typography
                        variant="h6"
                        sx={ { fontWeight: 500, fontSize: "1rem", mb: 2 } }
                      >
                        { " " }
                        {t("reservation.workflow.taskList")}
                      </Typography>
                      <TableContainer component={ Paper }>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t("reservation.workflow.task")}</TableCell>
                              <TableCell align="center">{t("reservation.workflow.completed")}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            { workflowTasks.map((task, index) => (
                              <TableRow key={ index }>
                                <TableCell>{ task.label }</TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={ task.done || false }
                                    onChange={ e => {
                                      const updatedTasks = [...workflowTasks];
                                      updatedTasks[index].done =
                                        e.target.checked;
                                      setWorkflowTasks(updatedTasks); // Mettre à jour l'état local
                                    } }
                                  />
                                </TableCell>
                              </TableRow>
                            )) }
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  ) }
                </div>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={ 12 } sm={ 12 } md={ 6 }>
            <Card>
              <CardContent sx={ { p: 2 } }>
                <Typography
                  variant="h6"
                  sx={ { fontWeight: 500, fontSize: "1.2rem", mb: 2 } }
                >
                  {t("reservation.fees.title")}
                </Typography>
                { /* Bouton pour ajouter des frais supplémentaires */ }
                <div style={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
                  <AddFeeDialog onAddFee={ handleAddFee } />
                </div>
                { /* Liste des frais supplémentaires */ }
                { fees.length > 0 && (
                  <TableContainer component={ Paper }>
                    <Table
                      aria-label="simple table"
                      sx={ { borderRadius: "4px" } }
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={ { fontWeight: 500 } }> {t("reservation.fees.name")}</TableCell>
                          <TableCell sx={ { fontWeight: 500 } } align="center">
                            {t("reservation.fees.amount")}
                          </TableCell>
                          <TableCell sx={ { fontWeight: 500 } } align="center">
                            {t("reservation.fees.action")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { fees.map((fee, index) => (
                          <TableRow key={ index }>
                            <TableCell>{ fee.feeName }</TableCell>
                            <TableCell align="right">
                              { fee.feeAmount } €
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                type="button"
                                color="secondary"
                                size="small"
                                onClick={ () => removeByIndex(index, "fee") }
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )) }
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) }
                { /* Bouton pour ajouter une remise */ }
                <div style={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
                  <AddDiscountDialog onAddDiscount={ handleAddDiscount } />
                </div>
                { /* Liste des remises */ }
                { discounts.length > 0 && (
                  <TableContainer component={ Paper }>
                    <Table aria-label="remises">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={ { fontWeight: 500 } }>
                            {t("reservation.fees.discountName")}
                          </TableCell>
                          <TableCell sx={ { fontWeight: 500 } } align="right">
                            {t("reservation.fees.amount")}
                          </TableCell>
                          <TableCell sx={ { fontWeight: 500 } } align="center">
                            {t("reservation.fees.action")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        { discounts.map((discount, index) => (
                          <TableRow key={ index }>
                            <TableCell>{ discount.discountName }</TableCell>
                            <TableCell align="right">
                              { discount.discountAmount }{ " " }
                              { discount.isPercentage ? "%" : "€" }
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                type="button"
                                color="secondary"
                                size="small"
                                onClick={ () => removeByIndex(index, "discount") }
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )) }
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) }
              </CardContent>
            </Card>
            <Card sx={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
              <CardContent sx={ { p: 2 } }>
                <Typography
                  variant="h6"
                  sx={ { fontWeight: 500, fontSize: "1.2rem", mb: 2 } }
                >
                  {t("reservation.price.title")}  
                </Typography>
                { /* Affichage du prix total */ }
                <TableContainer component={ Paper }>
                  <Table aria-label="remises">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={ { fontWeight: 500 } }>
                          {t("reservation.fees.description")} 
                        </TableCell>
                        <TableCell sx={ { fontWeight: 500 } } align="right">
                          {t("reservation.fees.amount")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{t("reservation.price.servicePrice")}</TableCell>
                        <TableCell align="right">{ servicePrice } €</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t("reservation.price.additionalFees")}</TableCell>
                        <TableCell align="right">
                          { fees.reduce((acc, fee) => acc + fee.feeAmount, 0) } €
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>{t("reservation.price.totalDiscounts")}</TableCell>
                        <TableCell align="right">
                          {calculateTotalDiscounts(servicePrice, fees, discounts)} €
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body1" sx={ { fontWeight: 600 } }>
                            {t("reservation.price.total")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" sx={ { fontWeight: 600 } }>
                            { totalPrice } €
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <div style={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
                  <div style={ { marginBottom: "1.3rem", marginTop: "1.2rem" } }>
                    <Grid container alignItems="center" spacing={ 2 }>
                      <Grid item>
                        <Typography
                          variant="body1"
                          sx={ { fontWeight: 500, fontSize: "1rem" } }
                        >
                          {t("reservation.payment.title")}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <TextField
                          select
                          name="paymentPlan"
                          value={ paymentPlan }
                          onChange={ handlePaymentPlanChange } // Met à jour la répartition et les montants en euros
                          sx={ { width: "120px" } }
                          disabled={hasPaidPayment} 
                        >
                          <MenuItem value="Non">{t("common.no")}</MenuItem>
                          <MenuItem value="2x">2x</MenuItem>
                          <MenuItem value="3x">3x</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </div>

                  { /* Affichage des lignes de paiement si paiement multiple */ }
                  { paymentPlan !== "Non" && (
                    <TableContainer
                      component={ Paper }
                      style={ { marginTop: "20px" } }
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("reservation.payment.number")}</TableCell>
                            <TableCell>{t("reservation.payment.distribution")}</TableCell>
                            <TableCell>{t("reservation.payment.amount")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>     
                          {reservationData && paymentDetails.map((payment, index) => (
                            <TableRow key={index}>
                              <TableCell>{`n°${index + 1}`}</TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  //value={payment.isPaid ? payment.percentage : paymentPercentages[index]}
                                  value={payment.isPaid ? paymentPercentages[index] : paymentPercentages[index]}
                                  onChange={e => {
                                    if (!payment.isPaid) {
                                      const newPercentages = [...paymentPercentages];
                                      newPercentages[index] = parseInt(e.target.value, 10);
                                      setPaymentPercentages(newPercentages);
                                    }
                                  }}
                                  disabled={payment.isPaid}
                                  name="paymentPercentage"
                                  id={`paymentPercentage_${index}`}    
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>
                                {payment.isPaid && (
                                  <Typography variant="caption" color="textSecondary">
                                    {payment.value} € (Réglé le {payment.paymentDate} par {payment.paymentMode})
                                  </Typography>
                                )}
                                {!payment.isPaid && (
                                  <TextField
                                    type="number"
                                    value={payment.value}
                                    onChange={e => {
                                      const updatedPayments = [...paymentDetails];
                                      updatedPayments[index] = {
                                        ...updatedPayments[index],
                                        value: e.target.value
                                      };
                                      setPaymentDetails(updatedPayments);
                                    }}
                                    name="paymentValue"
                                    id={`paymentValue_${index}`}                                    
                                    fullWidth
                                  />
                                ) }
                              </TableCell>
                            </TableRow>
                          ))} 
                          {!reservationData && paymentPercentages.map((percentage, index) => (
                            <TableRow key={index}>
                              <TableCell>{`n°${index + 1}`}</TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  value={percentage}
                                  onChange={e => {
                                    const newPercentages = [
                                      ...paymentPercentages
                                    ];
                                    newPercentages[index] = parseInt(
                                      e.target.value
                                    );
                                    setPaymentPercentages(newPercentages);
                                  }}
                                  fullWidth
                                />
                              </TableCell>
                              <TableCell>{paymentValues[index]} €</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) }
                </div>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={ 12 } sm={ 12 } md={ 6 }>
            <div>
              { /* Autres composants existants */ }
              <Button
                variant="contained"
                color="primary"
                onClick={ handleSaveClick }
                sx={ { marginTop: "20px" } }
              >
                {isEditing ? t("reservation.save.update") : t("reservation.save.create")}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default Reservation;
